/** Public venue details. Old events without locations still use venue + city. */
export interface Coordinates {
  lat: number;
  lng: number;
}
export interface ResolvedMapLink {
  coordinates: Coordinates;
  mapUrl?: string;
}
export interface TournamentLocation {
  id: string;
  name: string;
  address?: string;
  mapUrl?: string;
  coordinates?: Coordinates;
}
export const MAX_LOCATIONS = 12;
export const MAX_MAP_URL = 2048;

export function validCoordinates(lat: unknown, lng: unknown): boolean {
  return (
    typeof lat === "number" &&
    Number.isFinite(lat) &&
    Math.abs(lat) <= 90 &&
    typeof lng === "number" &&
    Number.isFinite(lng) &&
    Math.abs(lng) <= 180
  );
}

export function coordinatePair(value: string): Coordinates | null {
  const match = /^\s*([+-]?\d+(?:\.\d+)?)\s*[,;]\s*([+-]?\d+(?:\.\d+)?)\s*$/.exec(value);
  if (!match) return null;
  const lat = Number(match[1]);
  const lng = Number(match[2]);
  return validCoordinates(lat, lng) ? { lat, lng } : null;
}

export function mapUrl(value: string): URL | null {
  try {
    const url = new URL(value);
    return url.protocol === "https:" &&
      !url.username &&
      !url.password &&
      !url.port &&
      value.length <= MAX_MAP_URL
      ? url
      : null;
  } catch {
    return null;
  }
}

/** A place's pin beats the viewport (@lat,lng), which can be kilometres away.
 * Never turn a Google place's camera position into the venue's coordinates. */
export function coordinatesFromLink(value: string): Coordinates | null {
  const pair = coordinatePair(value);
  if (pair) return pair;
  const url = mapUrl(value);
  if (!url) return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(url.pathname + url.search + url.hash);
  } catch {
    return null;
  }
  const pin = /!3d([+-]?\d+(?:\.\d+)?)!4d([+-]?\d+(?:\.\d+)?)/.exec(decoded);
  if (pin) return coordinatePair(`${pin[1]},${pin[2]}`);
  for (const key of ["coordinate", "q", "query", "destination", "ll"]) {
    if (key === "ll" && !["maps.apple.com", "waze.com", "www.waze.com"].includes(url.hostname))
      continue;
    const parsed = coordinatePair(url.searchParams.get(key) ?? "");
    if (parsed) return parsed;
  }
  const osm = coordinatePair(`${url.searchParams.get("mlat")},${url.searchParams.get("mlon")}`);
  if (osm) return osm;
  const hash = /^#map=\d+\/([+-]?[\d.]+)\/([+-]?[\d.]+)$/.exec(url.hash);
  if (hash) return coordinatePair(`${hash[1]},${hash[2]}`);
  if (!url.pathname.includes("/place/")) {
    const camera = /@([+-]?\d+(?:\.\d+)?),([+-]?\d+(?:\.\d+)?)/.exec(decoded);
    if (camera) return coordinatePair(`${camera[1]},${camera[2]}`);
  }
  return null;
}

export function tournamentLocations(meta: {
  locations?: TournamentLocation[];
  venue?: string;
  city?: string;
}): TournamentLocation[] {
  return (
    meta.locations ??
    (meta.venue || meta.city
      ? [
          {
            id: "legacy-venue",
            name: meta.venue || meta.city || "",
            ...(meta.venue && meta.city ? { address: meta.city } : {}),
          },
        ]
      : [])
  );
}

export function mapChoices(location: TournamentLocation, appleDevice: boolean) {
  const coordinates = location.coordinates;
  const query = coordinates
    ? `${coordinates.lat},${coordinates.lng}`
    : [location.name, location.address].filter(Boolean).join(", ");
  const google = {
    name: "Google Maps",
    href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`,
  };
  const apple = {
    name: "Apple Maps",
    href: coordinates
      ? `https://maps.apple.com/?ll=${encodeURIComponent(query)}&q=${encodeURIComponent(location.name)}`
      : `https://maps.apple.com/?q=${encodeURIComponent(query)}`,
  };
  const osm = {
    name: "OpenStreetMap",
    href: coordinates
      ? `https://www.openstreetmap.org/?mlat=${coordinates.lat}&mlon=${coordinates.lng}#map=17/${coordinates.lat}/${coordinates.lng}`
      : `https://www.openstreetmap.org/search?query=${encodeURIComponent(query)}`,
  };
  return [...(appleDevice ? [apple, google] : [google, apple]), osm];
}
